<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FileSelector',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    url: {
      type: String,
      required: true,
    },
    payload: {
      type: Object,
      required: false,
    },
    accept: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const selectedFiles = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    selectedFiles: selectedFiles.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedFiles' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      selectedFiles,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('FileSelector|Header', 'Select file(s)'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.multiple,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('multiple');
    }
  );
  watch(
    () => props.url,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('url');
    }
  );
  watch(
    () => props.payload,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payload');
    }
  );
  watch(
    () => props.accept,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('accept');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_e31u1ci3s_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_102607404663001_closeModal_n24n0am6b_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_102607404663001_request_4pqx5tdb9_getActionArgs = async (
    $event
  ) => {
    return {
      url: props.url,
      method: 'POST',
      data: { ...props.payload, file: selectedFiles.value },
      contentType: 'multipart/form-data',
    };
  };

  const act_7315092382398562_closeModal_e31u1ci3s =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_e31u1ci3s_getActionArgs,
    });
  const inst_evh_7315092382398562_close_de33rxdpf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_e31u1ci3s,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_de33rxdpf],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_25av47mkw =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ymvhyb1oy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_25av47mkw,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ymvhyb1oy],
    event: 'reload',
  });
  const act_102607404663001_closeModal_n24n0am6b =
    new actionTypes.CloseModalAction({
      actionArgs: act_102607404663001_closeModal_n24n0am6b_getActionArgs,
    });
  const inst_evh_102607404663001_success_fntutrgkf =
    new actionTypes.EventHandlerImpl({
      action: act_102607404663001_closeModal_n24n0am6b,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_102607404663001_request_4pqx5tdb9 = new actionTypes.RequestAction({
    actionArgs: act_102607404663001_request_4pqx5tdb9_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_102607404663001_success_fntutrgkf],
  });
  const inst_evh_102607404663001_click_01h6cqnk6 =
    new actionTypes.EventHandlerImpl({
      action: act_102607404663001_request_4pqx5tdb9,
      event: 'click',
      displayName: 'submit',
    });
  const inst_evh_102607404663001_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_102607404663001_click_01h6cqnk6],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_de33rxdpf,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_ymvhyb1oy,
    inst_evh_7315092382398562_reload,
    inst_evh_102607404663001_success_fntutrgkf,
    inst_evh_102607404663001_click_01h6cqnk6,
    submit: inst_evh_102607404663001_click_01h6cqnk6,
    inst_evh_102607404663001_click,
  };

  const evh_7315092382398562_close_de33rxdpf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_de33rxdpf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_ymvhyb1oy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ymvhyb1oy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_102607404663001_success_fntutrgkf = async (event, scope) => {
    return await actions.inst_evh_102607404663001_success_fntutrgkf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_102607404663001_click_01h6cqnk6 = async (event, scope) => {
    return await actions.inst_evh_102607404663001_click_01h6cqnk6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_102607404663001_click = async (event, scope) => {
    return await actions.inst_evh_102607404663001_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="FileSelector"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <BCard no-body>
      <BTabs card>
        <BTab :title="pgettext('FileSelector', 'Browse')">
          <BCardBody>
            <BAlert show variant="danger" class="mb-3" v-if="!!error">
              {{
                _.escape(
                  _.isString(_.get(error, 'response.data.detail'))
                    ? _.get(error, 'response.data.detail')
                    : error.message || String(error)
                )
              }}
            </BAlert>
            <div class="row mb-3">
              <BFormFile
                :multiple="multiple"
                v-model="selectedFiles"
                :placeholder="
                  pgettext('FileSelector', 'Choose a file or drop it here')
                "
                :drop-placeholder="pgettext('FileSelector', 'Drop file here')"
                lang="hu"
                :accept="accept"
                class="col me-3"
              >
              </BFormFile>
              <ItpButton
                :disabled="!selectedFiles"
                :text="gettext('Upload')"
                icon="fa-upload"
                :spinning="actions.submit.isRunning"
                class="col-auto"
                @click="evh_102607404663001_click($event, {})"
              >
              </ItpButton>
            </div>
          </BCardBody>
        </BTab>
        <BTab :title="pgettext('FileSelector', 'Scanned Documents')">
          <BCardBody>
            <div class="row mb-3">
              <p class="text-center w-100">
                {{
                  pgettext(
                    'FileSelector',
                    'There are no documents in the scan folder.'
                  )
                }}
              </p>
            </div>
          </BCardBody>
        </BTab>
      </BTabs>
    </BCard>
  </ItpPage>
</template>
